import type { IntegrationAppListingField } from "@repo/shared-primitives"
import { createFileRoute, Link } from "@tanstack/react-router"
import { format } from "date-fns"
import { AppWindow, ArrowLeft, ExternalLink, Gavel } from "lucide-react"
import { useState } from "react"
import {
	DetailPanel,
	DetailPanelSection,
	PageHeader,
} from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
	ListingProposalDecisionForm,
	ListingProposalDecisionSummary,
} from "@/features/integration-app-listing-proposals/ListingProposalDecision"
import {
	ListingProposalDiff,
	type ListingProposalDiffReview,
} from "@/features/integration-app-listing-proposals/ListingProposalDiff"
import { ListingProposalStatusBadge } from "@/features/integration-app-listing-proposals/ListingProposalStatusBadge"
import { useListingProposal } from "@/queries/marketplace"
import type { GetListingProposalResponse } from "@/types/marketplace.types"

export const Route = createFileRoute(
	"/integration-apps/listing-requests/$proposalId",
)({
	component: ListingProposalPage,
})

function BackLink() {
	return (
		<Link
			to="/integration-apps/listing-requests"
			search={{} as never}
			className="inline-flex w-fit items-center gap-1 text-meta text-xs hover:text-foreground"
		>
			<ArrowLeft className="h-3.5 w-3.5" />
			Listing change requests
		</Link>
	)
}

function formatDateTime(value: string): string {
	return format(new Date(value), "MMM d, yyyy 'at' h:mm a")
}

/** Whether the app shows in the public directory today, in plain words. */
function directoryStatus(app: GetListingProposalResponse["app"]): string {
	if (!app.isPublic) {
		return "Not listed in the directory"
	}
	if (app.status !== "enabled") {
		return "Set to be listed, but hidden from the directory while the app is disabled"
	}
	return "Listed in the directory"
}

/**
 * Review of one owner-proposed listing change: the field-by-field Listing
 * panel on the left, the App and Decision panels on the right (stacked below lg).
 */
function ListingProposalPage() {
	const { proposalId } = Route.useParams()
	const { data, isLoading, error } = useListingProposal(proposalId)
	const [unticked, setUnticked] = useState<{
		proposalId: string
		fields: ReadonlySet<IntegrationAppListingField>
	}>({ proposalId, fields: new Set() })

	if (isLoading) {
		return (
			<div className="flex flex-col gap-4 p-4 sm:p-6">
				<Skeleton className="h-8 w-72 max-w-full" />
				<Skeleton className="h-64 w-full" />
			</div>
		)
	}

	if (error || !data) {
		return (
			<div className="flex flex-col gap-4 p-4 sm:p-6">
				<BackLink />
				<div className="rounded-lg bg-bad-soft px-4 py-3 text-bad">
					{error?.message ?? "Listing proposal not found"}
				</div>
			</div>
		)
	}

	const { proposal, app, diff } = data
	const isPending = proposal.status === "pending"
	const submitter = proposal.submittedBy

	const changedFields = diff
		.filter((entry) => entry.changed)
		.map((entry) => entry.field)
	const untickedFields =
		unticked.proposalId === proposalId
			? unticked.fields
			: new Set<IntegrationAppListingField>()
	const selectedFields = new Set(
		changedFields.filter((field) => !untickedFields.has(field)),
	)

	const setUntickedFields = (fields: ReadonlySet<IntegrationAppListingField>) =>
		setUnticked({ proposalId, fields })

	const review: ListingProposalDiffReview = isPending
		? {
				mode: "select",
				selected: selectedFields,
				onToggle: (field, selected) => {
					const next = new Set(untickedFields)
					if (selected) {
						next.delete(field)
					} else {
						next.add(field)
					}
					setUntickedFields(next)
				},
				onSelectAll: (selected) =>
					setUntickedFields(selected ? new Set() : new Set(changedFields)),
			}
		: proposal.acceptedFields && proposal.refusedFields
			? {
					mode: "outcome",
					accepted: proposal.acceptedFields,
					refused: proposal.refusedFields,
				}
			: { mode: "plain" }

	return (
		<div key={proposal.id} className="flex flex-col gap-6 p-4 sm:p-6">
			<div className="flex flex-col gap-3">
				<BackLink />
				<PageHeader
					title={
						<span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
							Listing change request
							<ListingProposalStatusBadge
								status={proposal.status}
								className="font-normal text-sm"
							/>
						</span>
					}
					description={
						<>
							Submitted by{" "}
							{submitter ? (
								<Link
									to="/users/$userId"
									params={{ userId: submitter.id }}
									className="text-foreground hover:text-primary-text hover:underline"
								>
									{submitter.username ? `@${submitter.username}` : "Unknown"}
								</Link>
							) : (
								"a deleted account"
							)}{" "}
							on{" "}
							<span className="tabular-nums">
								{formatDateTime(proposal.submittedAt)}
							</span>
						</>
					}
					actions={
						<Button variant="outline" asChild>
							<Link to="/integration-apps/$appId" params={{ appId: app.id }}>
								<ExternalLink className="h-4 w-4" />
								Open the app's admin page
							</Link>
						</Button>
					}
				/>
			</div>

			<div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-8">
				<ListingProposalDiff
					diff={diff}
					review={review}
					description={
						review.mode === "select"
							? "What accepting would change, compared with the live listing now. Untick a change to send it back to the owner; what they wrote stays in their form so they can fix it and submit again."
							: review.mode === "outcome"
								? "What was decided for each field the proposal changed. Accepted fields show the value applied to the listing; fields sent back show the live value against what the owner proposed."
								: "The proposed values compared with the live listing now, not at the time of the decision."
					}
				/>

				<div className="flex min-w-0 flex-col gap-4 lg:sticky lg:top-4">
					<DetailPanel title="App" icon={AppWindow}>
						<DetailPanelSection>
							<div className="flex items-center gap-3">
								{app.logoUrl ? (
									<img
										src={app.logoUrl}
										alt=""
										className="size-9 shrink-0 rounded-md object-cover"
									/>
								) : (
									<div className="size-9 shrink-0 rounded-md bg-muted" />
								)}
								<div className="flex min-w-0 flex-col">
									<span className="truncate font-medium">{app.name}</span>
									<span className="font-mono text-xs">{app.slug}</span>
								</div>
							</div>
						</DetailPanelSection>
						<DetailPanelSection label="Directory" className="gap-1 text-xs">
							<span className="text-muted-foreground">
								{directoryStatus(app)} ·{" "}
								{app.isFeatured ? "Featured" : "Not featured"}
							</span>
							<span className="text-meta">
								Accepting only updates the listing content. It does not list the
								app in the directory or feature it: both stay on{" "}
								<Link
									to="/integration-apps/$appId"
									params={{ appId: app.id }}
									className="text-foreground underline underline-offset-2 hover:text-primary-text"
								>
									the app's own admin page
								</Link>
								.
							</span>
						</DetailPanelSection>
					</DetailPanel>

					<DetailPanel title="Decision" icon={Gavel}>
						<DetailPanelSection>
							{isPending ? (
								<ListingProposalDecisionForm
									proposal={proposal}
									appName={app.name}
									changedFields={changedFields}
									selectedFields={selectedFields}
								/>
							) : (
								<ListingProposalDecisionSummary proposal={proposal} />
							)}
						</DetailPanelSection>
					</DetailPanel>
				</div>
			</div>
		</div>
	)
}
